import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import PasswordInput from "../components/PasswordInput";
import useSEO from "../hooks/useSEO";

export default function Login() {
  useSEO({ title: "Connexion", description: "Connectez-vous à votre compte DemkaBiz." });
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);
    try {
      const u = await login(email, pwd);
      toast.success(`Bienvenue ${u.name}`);
      const dest = u.role === "admin" ? "/admin"
        : ["professional", "entrepreneur", "organism"].includes(u.role) ? "/dashboard/provider"
        : (from !== "/" ? from : "/dashboard/client");
      navigate(dest, { replace: true });
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 403 && /v[ée]rifier votre adresse email/i.test(detail || "")) {
        setNeedsVerification(true);
      }
      toast.error(detail);
    } finally { setLoading(false); }
  };

  const resendVerification = async () => {
    if (!email) { toast.error("Veuillez saisir votre email."); return; }
    try {
      await api.post("/auth/resend-verification-public", { email });
      toast.success("Un nouveau lien de vérification a été envoyé.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2" data-testid="login-page">
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="login-form">
          <div>
            <h1 className="font-display font-bold text-4xl tracking-tight">Se connecter</h1>
            <p className="text-muted-foreground mt-2">Heureux de vous revoir.</p>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Email</label>
            <div className="relative mt-1">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Mot de passe</label>
            <div className="mt-1">
              <PasswordInput
                dataTestId="login-password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                required
                autoComplete="current-password"
                className="pl-4"
              />
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <Link to="/mot-de-passe-oublie" className="text-primary hover:underline" data-testid="forgot-password-link">Mot de passe oublié ?</Link>
          </div>
          {needsVerification && (
            <div className="rounded-xl border border-amber-300/50 bg-amber-50 p-4 text-sm" data-testid="needs-verification-banner">
              <p className="text-amber-900">Votre email n'est pas encore vérifié. Consultez votre boîte de réception (et vos spams).</p>
              <button type="button" onClick={resendVerification} className="text-primary font-medium hover:underline mt-2" data-testid="login-resend-verification">
                Renvoyer l'email de vérification
              </button>
            </div>
          )}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">
            {loading ? "Connexion…" : "Se connecter"} <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-sm text-center text-muted-foreground">
            Pas encore de compte ? <Link to="/inscription" className="text-primary font-medium hover:underline">Créer un compte</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:block relative bg-primary">
        <img src="https://images.unsplash.com/photo-1589422493788-9a1378a6f6e1?w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/95 to-emerald-900/95" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-display font-bold text-5xl tracking-tight leading-tight">DemkaBiz</h2>
          <p className="italic font-light text-xl mt-2">S'établir. Entreprendre. Réussir.</p>
          <p className="text-white/80 max-w-md mt-4">La plateforme francophone qui connecte les nouveaux arrivants, entrepreneurs et professionnels au Canada.</p>
        </div>
      </div>
    </div>
  );
}
